import store from '@state/store';
import ReportModel from '@models/Report';

export default [
  {
    path: '/',
    name: 'home.index',
    component: () => lazyLoadView(import('@views/home/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/login',
    name: 'login.index',
    component: () => lazyLoadView(import('@views/auth/login')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/register',
    name: 'register.index',
    component: () => lazyLoadView(import('@views/auth/register')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/forgot-password',
    name: 'forgot.index',
    component: () => lazyLoadView(import('@views/auth/forgot')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/reset-password/:token',
    name: 'reset.index',
    component: () => lazyLoadView(import('@views/auth/reset')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/email-verify/:token',
    name: 'verify.index',
    component: () => lazyLoadView(import('@views/auth/verify')),
    meta: {
      guestRequired: true,
    },
  },
  {
    path: '/news',
    name: 'news.index',
    component: () => lazyLoadView(import('@views/news/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/downloads',
    name: 'downloads.index',
    component: () => lazyLoadView(import('@views/downloads/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/point-of-interests',
    name: 'point-of-interests.index',
    component: () => lazyLoadView(import('@views/pointOfInterests/index')),
    meta: {
      authRequired: true,
    },
  },  
  {
    path: '/news/:id',
    name: 'news.show',
    component: () => lazyLoadView(import('@views/news/show')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/reports',
    name: 'reports.index',
    component: () => lazyLoadView(import('@views/reports/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/calendar',
    name: 'calendar.index',
    component: () => lazyLoadView(import('@views/calendar/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/reports/create',
    name: 'reports.create',
    component: () => lazyLoadView(import('@views/reports/create')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/reports/:id/edit',
    name: 'reports.edit',
    component: () => lazyLoadView(import('@views/reports/edit')),
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        ReportModel.find(routeTo.params.id)
          .then((response) => {
            routeTo.params.report = response.report;
            routeTo.params.carUses = response.car_uses;
            routeTo.params.serviceTimes = response.service_times.data;
            routeTo.params.expenses = response.expenses;
            routeTo.params.reportsCount = response.reports_count;
            routeTo.params.dailyAllowances = response.daily_allowances;
            routeTo.params.allExpensesHaveScan = response.all_expenses_have_scan;
            next();
          })
          .catch(() => next({ name: 'reports.index' }));
      },
    },
    props: (route) => ({
      report: route.params.report,
      carUses: route.params.carUses,
      serviceTimes: route.params.serviceTimes,
      expenses: route.params.expenses,
      reportsCount: route.params.reportsCount,
      dailyAllowances: route.params.dailyAllowances,
      summedServiceTime: route.params.summedServiceTime,
      allExpensesHaveScan: route.params.allExpensesHaveScan,
    }),
  },
  {
    path: '/profile',
    name: 'profile.index',
    component: () => lazyLoadView(import('@views/profile/profile')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut');
        const authRequiredOnPreviousRoute = routeFrom.matched.some((route) => route.meta.authRequired);
        // Navigate back to previous page, or home as a fallback
        next(authRequiredOnPreviousRoute ? { name: 'login.index' } : { ...routeFrom });
      },
    },
  },
  // leave
  {
    path: '/leaves',
    name: 'leaves.index',
    component: () => lazyLoadView(import('@views/leaves/index')),
    meta: {
      authRequired: true,
    },
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/errors/_404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
];

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@views/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  });

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children);
    },
  });
}
